// frontend/src/pages/OrgSettingsPage.tsx
// Organization Settings page for admin role users to view/edit their own org

import React, { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { formatDate, cn } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';
import { StorageSettings } from '@/components/orgSettings/StorageSettings';
import { AISettings } from '@/components/orgSettings/AISettings';
import { Microsoft365Settings } from '@/components/orgSettings/Microsoft365Settings';

type TabId = 'general' | 'microsoft365' | 'storage' | 'ai';

const TABS: Array<{ id: TabId; label: string; description: string }> = [
  { id: 'general', label: 'General', description: 'Org name, AI toggle, issue tags' },
  { id: 'microsoft365', label: 'Microsoft 365', description: 'Tenant app registration & mailbox sync' },
  { id: 'storage', label: 'Storage', description: 'Customer-managed S3 / Azure Blob / GCS' },
  { id: 'ai', label: 'AI Providers', description: 'Customer OpenAI / Claude / etc. API keys' }
];

export const OrgSettingsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [org, setOrg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // General tab form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    isAIEnabled: false,
  });

  // Issue Tags state
  const [issueTags, setIssueTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [savingTags, setSavingTags] = useState(false);

  const orgId = typeof user?.organization === 'object' ? (user?.organization as any)?._id : user?.organization;

  const fetchOrg = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.organizations.get(orgId);
      const data = (result as any)?.data || result;
      setOrg(data);
      setForm({
        name: data.name || '',
        description: data.description || '',
        isAIEnabled: data.isAIEnabled || false,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const fetchIssueTags = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken') || (() => {
        const store = localStorage.getItem('auth-store');
        return store ? JSON.parse(store)?.state?.token : null;
      })();
      const res = await fetch('/api/organization-customization/issue-tags', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setIssueTags(data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchOrg();
    fetchIssueTags();
  }, [fetchOrg, fetchIssueTags]);

  // Honor ?tab=... deep-links so the M365 admin-consent return URL can land on
  // the right pane.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab') as TabId | null;
    if (t && TABS.find(x => x.id === t)) {
      setActiveTab(t);
    } else if (params.get('ms365_consent')) {
      setActiveTab('microsoft365');
    }
  }, []);

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (tag && !issueTags.includes(tag)) {
      setIssueTags(prev => [...prev, tag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setIssueTags(prev => prev.filter(t => t !== tag));
  };

  const handleSaveTags = async () => {
    setSavingTags(true);
    try {
      const token = localStorage.getItem('authToken') || (() => {
        const store = localStorage.getItem('auth-store');
        return store ? JSON.parse(store)?.state?.token : null;
      })();
      const res = await fetch('/api/organization-customization/issue-tags', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: issueTags })
      });
      const data = await res.json();
      if (data.success) {
        addNotification?.({ type: 'success', title: 'Issue Tags Updated', message: data.message });
      } else {
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Save Failed', message: err.message });
    } finally {
      setSavingTags(false);
    }
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await apiClient.organizations.update(orgId, form);
      addNotification?.({ type: 'success', title: 'Settings Updated', message: 'Organization settings saved successfully.' });
      fetchOrg();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Save Failed', message: err?.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'implementor')) {
    return (
      <PageLayout title="Organization Settings" subtitle="Access denied">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Only administrators can access organization settings.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Organization Settings" subtitle="Manage your organization's configuration">
      <div className="max-w-4xl space-y-6">
        {error && <ErrorAlert message={error} onClose={() => setError(null)} />}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-1" aria-label="Settings tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'py-3 px-1 border-b-2 text-sm font-medium whitespace-nowrap transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === 'general' ? (
          loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : org ? (
            <>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">General Information</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="aiEnabled"
                    checked={form.isAIEnabled}
                    onChange={(e) => setForm(prev => ({ ...prev, isAIEnabled: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="aiEnabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Enable AI Features (classification, extraction)
                  </label>
                </div>

                <div className="pt-3 flex justify-end">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? <><LoadingSpinner size="sm" /> Saving...</> : 'Save Changes'}
                  </Button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Organization Details</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Subscription Plan</span>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">{org.subscriptionPlan || 'Basic'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Status</span>
                    <p className={`font-medium ${org.isActive ? 'text-green-600' : 'text-red-600'}`}>
                      {org.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Created</span>
                    <p className="font-medium text-gray-900 dark:text-white">{org.createdAt ? formatDate(org.createdAt, false) : '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">AI Features</span>
                    <p className="font-medium text-gray-900 dark:text-white">{org.isAIEnabled ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Issue Tags</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage the master list of issue tags available for correspondences.</p>

                <div className="flex flex-wrap gap-2">
                  {issueTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 text-blue-400 hover:text-red-500 font-bold"
                        title={`Remove "${tag}"`}
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                    placeholder="Add new tag..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={50}
                  />
                  <Button onClick={handleAddTag} disabled={!newTag.trim()}>
                    Add
                  </Button>
                </div>

                <div className="pt-2 flex justify-end">
                  <Button onClick={handleSaveTags} disabled={savingTags}>
                    {savingTags ? <><LoadingSpinner size="sm" /> Saving...</> : 'Save Issue Tags'}
                  </Button>
                </div>
              </div>

              {org.resourceLimits && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Resource Limits</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Contact your system administrator to adjust these limits.</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {Object.entries(org.resourceLimits).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-gray-500 dark:text-gray-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <p className="font-medium text-gray-900 dark:text-white">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null
        ) : activeTab === 'microsoft365' ? (
          orgId ? <Microsoft365Settings orgId={orgId} /> : null
        ) : activeTab === 'storage' ? (
          <StorageSettings />
        ) : activeTab === 'ai' ? (
          <AISettings />
        ) : null}
      </div>
    </PageLayout>
  );
};
